import {
  Box,
  HStack,
  Skeleton,
  Text,
  VStack,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
} from '@chakra-ui/react';
import { useCallback } from 'react';
import { RouterLink } from '@gamma/ui/navigation';
import MoreVertSharpIcon from '@gamma/ui/icons/MoreVertSharpIcon';
import SearchSharpIcon from '@gamma/ui/icons/SearchSharpIcon';
import { ROUTES } from '@investigator/shared/constants';
import { EntityCommunicationPodVariant } from '../../types/entityCommunication';
import { POD_WIDTH, SHADOW_OFFSET } from '../../utils/entityCommunication';
import { useEntityCommunicationCard } from '../../hooks';
import { EntityCommunicationPopover } from './EntityCommunicationPopover';

interface EntityCommunicationCardComponentProps {
  variant: EntityCommunicationPodVariant;
}

export const EntityCommunicationCard = ({
  variant,
}: EntityCommunicationCardComponentProps): JSX.Element => {
  const {
    shadowBg,
    Icon,
    backgroundColor,
    borderColor,
    showShadow,
    isClickable,
    primaryText,
    secondaryText,
    entities,
    isLoading,
    logSearchQuery,
    direction,
  } = useEntityCommunicationCard(variant);

  const { isOpen, onToggle, onClose } = useDisclosure();

  const handleCardClick = useCallback((): void => {
    if (isClickable) {
      onToggle();
    }
  }, [isClickable, onToggle]);

  return (
    <Box position="relative" w={`${POD_WIDTH}px`}>
      {/* Shadow/offset box behind the main pod */}
      {showShadow && (
        <Box
          position="absolute"
          top={`${SHADOW_OFFSET}px`}
          left={`${SHADOW_OFFSET}px`}
          w="100%"
          h="100%"
          bg={shadowBg}
          borderRadius="md"
          zIndex={0}
        />
      )}

      {/* Main pod */}
      <Box
        data-testid={`entity-communication-card-${variant}`}
        position="relative"
        bg={backgroundColor}
        borderRadius="md"
        border="1px solid"
        borderColor={borderColor}
        p={3}
        zIndex={1}
        cursor={isClickable ? 'pointer' : 'default'}
        onClick={handleCardClick}
        transition="background 0.2s ease"
        _hover={isClickable ? { bg: 'layer.3' } : undefined}
      >
        <HStack spacing={3} align="center">
          {/* Icon container */}
          <Box
            bg="blackAlpha.200"
            borderRadius="md"
            p={2}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon boxSize={5} />
          </Box>

          {/* Text content */}
          <VStack
            align="start"
            spacing={0}
            flex={1}
            data-testid={`entity-communication-card-text-${variant}`}
          >
            {isLoading && variant !== EntityCommunicationPodVariant.CURRENT ? (
              <>
                <Skeleton height="16px" width="80px" />
                <Skeleton height="14px" width="120px" mt={1} />
              </>
            ) : (
              <>
                <Text
                  fontSize="14px"
                  fontWeight="semibold"
                  color="text.primary"
                  lineHeight="20px"
                >
                  {primaryText}
                </Text>
                <Text
                  fontSize="14px"
                  color="text.secondary"
                  lineHeight="20px"
                  noOfLines={1}
                >
                  {secondaryText}
                </Text>
              </>
            )}
          </VStack>
          <Menu
            placement="bottom-end"
            data-testid={`entity-communication-card-menu-${variant}`}
          >
            <MenuButton
              as={IconButton}
              aria-label="More options"
              icon={<MoreVertSharpIcon />}
              variant="ghost"
              size="xs"
              minW="auto"
              p={1}
              color="text.primary"
              _hover={{ bg: 'blackAlpha.200' }}
              onClick={(e) => e.stopPropagation()}
            />
            <MenuList>
              <MenuItem
                as={RouterLink}
                to={{
                  pathname: ROUTES.logSearch,
                  search: logSearchQuery,
                }}
                target="_blank"
                icon={<SearchSharpIcon />}
                onClick={(e) => e.stopPropagation()}
              >
                View in log search
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Box>

      {/* Entity list popover - controlled by card click */}
      {isClickable && direction && (
        <EntityCommunicationPopover
          direction={direction}
          entities={entities}
          isOpen={isOpen}
          onClose={onClose}
        />
      )}
    </Box>
  );
};
